import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import CKEditorWithSource from "./CKEditorWithSource";
import axios from "axios";
import config from "../coreFIles/config";
import { getAdminAuthToken } from "../coreFIles/auth";
import {
  adminCourseGetAction,
  adminCourseWeekCreateAction,
  adminCourseWeekGetAction,
  adminCourseWeekUpdateAction
} from "../Action/action";

function resolvePublicUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  const s = String(pathOrUrl);
  if (/^https?:\/\//i.test(s)) return s;
  let origin = "";
  try {
    if (config.apiUrl) origin = new URL(config.apiUrl).origin;
  } catch (e) {
    origin = "";
  }
  if (!origin) origin = "http://localhost:3001";
  if (s.startsWith("/")) return `${origin}${s}`;
  return `${origin}/${s}`;
}

function editorPlainText(html) {
  return String(html || "")
    .replace(/&nbsp;/g, " ")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function buildApiUrl(endpoint) {
  const base = (config.apiUrl || "").replace(/\/+$/, "");
  const path = String(endpoint || "").replace(/^\/+/, "");
  return base ? `${base}/${path}` : `/${path}`;
}

class CkUploadAdapter {
  constructor(loader) {
    this.loader = loader;
    this.controller = new AbortController();
  }

  abort() {
    try {
      this.controller.abort();
    } catch (e) {}
  }

  async upload() {
    const file = await this.loader.file;
    const fd = new FormData();
    fd.append("upload", file);

    const token = getAdminAuthToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    const resp = await axios.post(buildApiUrl("/admin/upload/image"), fd, {
      headers,
      withCredentials: true,
      signal: this.controller.signal
    });

    const url = resp?.data?.url;
    if (!url) throw new Error("Upload failed");
    return { default: resolvePublicUrl(url) };
  }
}

function CkUploadAdapterPlugin(editor) {
  // eslint-disable-next-line no-param-reassign
  editor.plugins.get("FileRepository").createUploadAdapter = (loader) => new CkUploadAdapter(loader);
}

export default function CourseWeekForm({ mode }) {
  const { courseId, weekId } = useParams();
  const navigate = useNavigate();
  const isEdit = mode === "edit";

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const [courseTitle, setCourseTitle] = useState("Course");

  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [headingTitle, setHeadingTitle] = useState("");
  const [descriptionTitle, setDescriptionTitle] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [description, setDescription] = useState("");
  const [position, setPosition] = useState("");
  const [iconUrl, setIconUrl] = useState("");
  const [iconFile, setIconFile] = useState(null);
  const [iconPreviewUrl, setIconPreviewUrl] = useState("");
  const iconInputRef = useRef(null);
  const [weekImageUrl, setWeekImageUrl] = useState("");
  const [weekImageFile, setWeekImageFile] = useState(null);
  const [weekImagePreviewUrl, setWeekImagePreviewUrl] = useState("");
  const weekImageInputRef = useRef(null);

  useEffect(() => {
    if (!iconFile) {
      if (iconPreviewUrl) setIconPreviewUrl("");
      return undefined;
    }
    const url = URL.createObjectURL(iconFile);
    setIconPreviewUrl(url);
    return () => {
      try {
        URL.revokeObjectURL(url);
      } catch (e) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iconFile]);

  useEffect(() => {
    if (!weekImageFile) {
      if (weekImagePreviewUrl) setWeekImagePreviewUrl("");
      return undefined;
    }
    const url = URL.createObjectURL(weekImageFile);
    setWeekImagePreviewUrl(url);
    return () => {
      try {
        URL.revokeObjectURL(url);
      } catch (e) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekImageFile]);

  useEffect(() => {
    let cancelled = false;
    async function loadCourse() {
      if (!courseId) return;
      try {
        const res = await adminCourseGetAction(courseId);
        if (cancelled) return;
        if (res?.success && res?.data?.title) setCourseTitle(res.data.title);
      } catch (e) {
        // ignore
      }
    }
    loadCourse();
    return () => {
      cancelled = true;
    };
  }, [courseId]);

  useEffect(() => {
    let cancelled = false;
    async function loadWeek() {
      if (!isEdit) return;
      setLoading(true);
      setError("");
      try {
        const res = await adminCourseWeekGetAction(weekId);
        if (cancelled) return;
        if (!res?.success) {
          setError(res?.message || res?.msg || "Failed to load week.");
          return;
        }
        const w = res.data || {};
        setTitle(w.title || "");
        setSubtitle(w.subtitle || "");
        setHeadingTitle(w.heading_title || "");
        setDescriptionTitle(w.description_title || "");
        setShortDescription(w.short_description || "");
        setDescription(w.description || "");
        setPosition(w.position !== undefined && w.position !== null ? String(w.position) : "");
        setIconUrl(w.icon || "");
        setIconFile(null);
        setWeekImageUrl(w.week_image || "");
        setWeekImageFile(null);
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.message || e?.message || "Failed to load week.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadWeek();
    return () => {
      cancelled = true;
    };
  }, [isEdit, weekId]);

  const validate = () => {
    const errs = {};
    if (!String(title || "").trim()) errs.title = "Week Title is required.";
    if (!editorPlainText(shortDescription)) errs.shortDescription = "Short Description is required.";
    if (!editorPlainText(description)) errs.description = "Description is required.";
    if (position !== "" && position !== null && position !== undefined) {
      const n = Number(position);
      if (Number.isNaN(n) || n < 0) errs.position = "Please enter a valid position.";
    }
    return errs;
  };

  const onSave = async () => {
    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length) return;

    setSaving(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("title", String(title || "").trim());
      formData.append("subtitle", String(subtitle || "").trim());
      formData.append("heading_title", String(headingTitle || "").trim());
      formData.append("description_title", String(descriptionTitle || "").trim());
      formData.append("short_description", shortDescription || "");
      formData.append("description", description || "");
      if (position !== "") formData.append("position", String(Number(position)));
      if (iconFile) {
        formData.append("icon", iconFile);
      } else if (iconUrl) {
        formData.append("icon", iconUrl);
      } else {
        formData.append("icon", "");
      }
      if (weekImageFile) {
        formData.append("week_image", weekImageFile);
      } else if (weekImageUrl) {
        formData.append("week_image", weekImageUrl);
      } else {
        formData.append("week_image", "");
      }

      const res = isEdit
        ? await adminCourseWeekUpdateAction(weekId, formData)
        : await adminCourseWeekCreateAction(courseId, formData);

      if (!res?.success) throw new Error(res?.message || res?.msg || "Save failed");
      toast.success(`Week ${isEdit ? "updated" : "created"}`, { toastId: "week-save" });
      navigate(`/courses/${courseId}/weeks`);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Save failed";
      setError(msg);
      toast.error(msg, { toastId: "week-save" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="content">
      {isEdit && loading ? (
        <div className="loading-overlay" role="status" aria-live="polite" aria-busy="true">
          <div className="loading-spinner" aria-hidden="true" />
        </div>
      ) : null}

      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">⚠️</span>
          <div className="banner-text">
            <strong>Could not load/save week.</strong> {error}
          </div>
        </div>
      ) : null}

      <div className="flex items-center justify-between" style={{ marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
        <div className="breadcrumb" style={{ marginBottom: 0 }}>
          <Link to="/courses">Courses</Link>
          <span>›</span>
          <Link to={`/courses/${courseId}/edit`}>{courseTitle}</Link>
          <span>›</span>
          <Link to={`/courses/${courseId}/weeks`}>Manage Weeks</Link>
          <span>›</span>
          <span>{isEdit ? "Edit Week" : "Add Week"}</span>
        </div>
        <div className="flex items-center gap-3">
          <Link to={`/courses/${courseId}/weeks`} className="btn btn-outline btn-sm">
            ← Back
          </Link>
        </div>
      </div>

      <div className="grid-2">
        <div>
          <div className="card mb-6">
            <h2 className="section-title">Week Details</h2>

            <div className="form-group">
              <label>
                Week Title <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="Enter week title..."
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  setFieldErrors((p) => ({ ...p, title: "" }));
                }}
                aria-invalid={!!fieldErrors.title}
                style={fieldErrors.title ? { borderColor: "var(--danger)" } : undefined}
              />
              {fieldErrors.title ? (
                <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                  {fieldErrors.title}
                </div>
              ) : null}
            </div>
            
            <div className="form-group">
              <label>Subtitle</label>
              <input
                type="text"
                className="form-control"
                placeholder="Optional subtitle..."
                value={subtitle}
                onChange={(e) => setSubtitle(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Heading Title</label>
              <input
                type="text"
                className="form-control"
                placeholder="Optional heading title..."
                value={headingTitle}
                onChange={(e) => setHeadingTitle(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Description title</label>
              <input
                type="text"
                className="form-control"
                placeholder="Heading shown above the week description (optional)"
                value={descriptionTitle}
                onChange={(e) => setDescriptionTitle(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>
                Short Description <span className="text-danger">*</span>
              </label>
              <CKEditorWithSource
                heightClassName="ckeditor-height-200"
                containerStyle={
                  fieldErrors.shortDescription
                    ? { border: "1px solid var(--danger)", borderRadius: 6, overflow: "hidden" }
                    : { border: "1px solid var(--gray-300)", borderRadius: 6, overflow: "hidden" }
                }
                config={{ extraPlugins: [CkUploadAdapterPlugin] }}
                data={shortDescription}
                onChange={(html) => {
                  setShortDescription(html);
                  setFieldErrors((p) => ({ ...p, shortDescription: "" }));
                }}
              />
              {fieldErrors.shortDescription ? (
                <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                  {fieldErrors.shortDescription}
                </div>
              ) : null}
            </div>

            <div className="form-group">
              <label>
                Description <span className="text-danger">*</span>
              </label>
              <CKEditorWithSource
                heightClassName="ckeditor-height-400"
                containerStyle={
                  fieldErrors.description
                    ? { border: "1px solid var(--danger)", borderRadius: 6, overflow: "hidden" }
                    : { border: "1px solid var(--gray-300)", borderRadius: 6, overflow: "hidden" }
                }
                config={{ extraPlugins: [CkUploadAdapterPlugin] }}
                data={description}
                onChange={(html) => {
                  setDescription(html);
                  setFieldErrors((p) => ({ ...p, description: "" }));
                }}
              />
              {fieldErrors.description ? (
                <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                  {fieldErrors.description}
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div>
          <div className="card mb-6">
            <h2 className="section-title">Settings</h2>
            
            <div className="form-group">
              <label>Position (optional)</label>
              <input
                type="number"
                className="form-control"
                placeholder="e.g. 1"
                min="0"
                value={position}
                onChange={(e) => {
                  setPosition(e.target.value);
                  setFieldErrors((p) => ({ ...p, position: "" }));
                }}
                aria-invalid={!!fieldErrors.position}
                style={fieldErrors.position ? { borderColor: "var(--danger)" } : undefined}
              />
              {fieldErrors.position ? (
                <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                  {fieldErrors.position}
                </div>
              ) : null}
            </div>

            <div className="form-group">
              <label>Week icon</label>
              <p className="small-label" style={{ marginBottom: 8 }}>
                Small image shown in the week banner.
              </p>
              <input
                ref={iconInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                  setIconFile(file);
                }}
              />
              <div
                className="upload-area p-sm"
                role="button"
                tabIndex={0}
                onClick={() => iconInputRef.current?.click?.()}
                onKeyDown={(e) => e.key === "Enter" && iconInputRef.current?.click?.()}
                aria-label="Upload week icon"
              >
                {iconPreviewUrl || iconUrl ? (
                  <img
                    src={iconPreviewUrl || resolvePublicUrl(iconUrl)}
                    alt="Week icon"
                    style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 12, display: "block" }}
                  />
                ) : (
                  <div className="logo-display">ICON</div>
                )}
                <p className="font-sm">{iconFile ? iconFile.name : "Click to upload week icon"}</p>
              </div>
              {iconUrl || iconFile ? (
                <button
                  type="button"
                  className="btn btn-outline btn-sm mt-4"
                  onClick={() => {
                    setIconFile(null);
                    setIconUrl("");
                    if (iconInputRef.current) iconInputRef.current.value = "";
                  }}
                >
                  Remove icon
                </button>
              ) : null}
            </div>

            <div className="form-group">
              <label>Week image</label>
              <p className="small-label" style={{ marginBottom: 8 }}>
                Larger image used for the week in the app.
              </p>
              <input
                ref={weekImageInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                  setWeekImageFile(file);
                }}
              />
              <div
                className="upload-area p-sm"
                role="button"
                tabIndex={0}
                onClick={() => weekImageInputRef.current?.click?.()}
                onKeyDown={(e) => e.key === "Enter" && weekImageInputRef.current?.click?.()}
                aria-label="Upload week image"
              >
                {weekImagePreviewUrl || weekImageUrl ? (
                  <img
                    src={weekImagePreviewUrl || resolvePublicUrl(weekImageUrl)}
                    alt="Week"
                    style={{ width: 120, height: 120, objectFit: "cover", borderRadius: 12, display: "block" }}
                  />
                ) : (
                  <div className="logo-display">IMG</div>
                )}
                <p className="font-sm">{weekImageFile ? weekImageFile.name : "Click to upload week image"}</p>
              </div>
              {weekImageUrl || weekImageFile ? (
                <button
                  type="button"
                  className="btn btn-outline btn-sm mt-4"
                  onClick={() => {
                    setWeekImageFile(null);
                    setWeekImageUrl("");
                    if (weekImageInputRef.current) weekImageInputRef.current.value = "";
                  }}
                >
                  Remove image
                </button>
              ) : null}
            </div>
          </div>

          <div className="card">
            <h2 className="section-title">Summary</h2>
            <div className="summary-meta">
              <div className="flex justify-between">
                <span>Course:</span> <span>{courseTitle || "—"}</span>
              </div>
              <div className="flex justify-between">
                <span>Week:</span> <span>{title?.trim() || "—"}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="form-actions">
        <button className="btn btn-primary" type="button" onClick={() => onSave()} disabled={saving || loading}>
          {saving ? (
            "Saving…"
          ) : (
            <>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z" />
                <path d="M17 21v-8H7v8" />
                <path d="M7 3v5h8" />
              </svg>
              Save Week
            </>
          )}
        </button>
        <Link to={`/courses/${courseId}/weeks`} className="btn btn-outline">
          Cancel
        </Link>
      </div>
    </div>
  );
}

